import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { DataTableColumnHeader } from '@/components/tables/DataTableColumnHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import type {
  SalesSummaryReportActivityKind,
  SalesSummaryReportOrderActivityRow,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { formatTransactionDate } from './-formatters'
import { SectionCardHeader } from './-section-card-header'

const activityLabels: Record<SalesSummaryReportActivityKind, string> = {
  sale: 'Order placed',
  order_change: 'Order changed',
  refund: 'Refund',
  cancellation: 'Cancellation',
  refund_reversal: 'Refund canceled',
  adjustment: 'Incoming wire order adjustment',
  shop_fee: 'Shop fee',
  shop_fee_refund: 'Shop fee refund',
  shop_fee_refund_reversal: 'Shop fee refund canceled',
  shop_fee_cancellation: 'Shop fee cancellation',
  outgoing_wire_sent: 'Outgoing wire sent',
  outgoing_wire_canceled: 'Outgoing wire canceled',
}

type OrderActivityCardProps = {
  rows: SalesSummaryReportOrderActivityRow[]
  orgId: number
  /** The report's timezone (`filters.timezone`). */
  timezone: string
}

export function OrderActivityCard({ rows, orgId, timezone }: OrderActivityCardProps) {
  const path = usePathHelper()
  const columns: ColumnDef<SalesSummaryReportOrderActivityRow>[] = [
    {
      accessorKey: 'created_at',
      meta: { label: 'Date' },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => formatTransactionDate(row.original.created_at, timezone),
    },
    {
      accessorKey: 'sales_order_id',
      meta: { label: 'Order' },
      header: 'Order',
      cell: ({ row }) =>
        row.original.sales_order_id ? (
          <Typography
            as={Link}
            {...path.salesOrders.show(row.original.sales_order_id).using({ orgId }).link}
            variant="bodySmall"
            tone="interaction"
            interaction="hoverUnderline"
          >
            #{row.original.sales_order_id}
          </Typography>
        ) : (
          '—'
        ),
    },
    {
      accessorKey: 'customer_name',
      meta: { label: 'Customer' },
      header: 'Customer',
      cell: ({ row }) =>
        row.original.purchaser_customer_id ? (
          <Typography
            as={Link}
            {...path.customers.show(row.original.purchaser_customer_id).using({ orgId }).link}
            variant="bodySmall"
            tone="interaction"
            interaction="hoverUnderline"
          >
            {row.original.customer_name}
          </Typography>
        ) : (
          row.original.customer_name || '—'
        ),
    },
    {
      accessorKey: 'activity_kind',
      meta: { label: 'Activity' },
      header: 'Activity',
      cell: ({ row }) => activityLabels[row.original.activity_kind],
    },
    {
      accessorKey: 'total_sales',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Sales" />,
      cell: ({ row }) => (
        <span className="tabular-nums">{formatCurrency(row.original.total_sales)}</span>
      ),
      meta: { label: 'Sales', kind: 'money', align: 'end' },
    },
  ]
  return (
    <Card>
      <SectionCardHeader title="Order activity" />
      <CardContent>
        <IndexDataTable
          mode="client"
          data={rows}
          columns={columns}
          pagination
          initialSorting={[{ id: 'created_at', desc: true }]}
          getRowId={(row) => row.activity_id}
        />
      </CardContent>
    </Card>
  )
}
